import { createLogEvent } from 'shared/logger';

/**
 * Frontend application log events shared across hendrx and core packages
 * Namespace: frontend.*
 *
 * Used by: hendrx app, core widgets, shared components
 *
 * IMPORTANT: Frontend logging is expensive (each log = HTTP request to API).
 *
 * Only log:
 * ✅ Critical errors blocking students
 * ✅ Severe performance issues (>2s delays)
 * ✅ Issues NOT already logged by API calls
 *
 * DO NOT log:
 * ❌ API calls (backend already logs these)
 * ❌ User interactions (creates noise, often duplicates API logs)
 * ❌ Page views (use analytics tools like Google Analytics)
 * ❌ Successful operations (only failures)
 * ❌ Navigation success (only errors)
 *
 * How it works:
 * - Frontend calls logFrontendEventApi({ eventType: FrontendSharedEvents.error.widget_crash, ... })
 * - Helper sends POST to /api/internal/player-logs
 * - API logs the event with backend logger
 */
export const FrontendSharedEvents = {
  // Error events - critical errors blocking students
  error: {
    /**
     * Widget crashed - student loses work
     * When: ErrorBoundary catches error in content object widget
     */
    widgetCrash: createLogEvent('frontend.error', 'widget_crash'),

    /**
     * Page crashed - entire app broken
     * When: Global ErrorBoundary catches unhandled error
     */
    pageCrash: createLogEvent('frontend.error', 'page_crash'),

    /**
     * Unhandled error - caught by global error handler
     * When: window.onerror or unhandledrejection event
     */
    unhandledError: createLogEvent('frontend.error', 'unhandled_error'),

    /**
     * Navigation error - cannot reach destination
     * When: Route navigation fails
     */
    navigationError: createLogEvent('frontend.error', 'navigation_error')
  },

  // Performance events - severe issues impacting students
  performance: {
    /**
     * Slow render - component taking >2s to render (blocks interaction)
     * When: Component render time exceeds 2000ms
     */
    slowRender: createLogEvent('frontend.performance', 'slow_render'),

    /**
     * Memory warning - browser at >90% memory (risk of crash)
     * When: performance.memory.usedJSHeapSize > 90% of limit
     */
    memoryWarning: createLogEvent('frontend.performance', 'memory_warning'),

    /**
     * Main thread blocked - UI frozen for >3s
     * When: Long task blocks main thread for >3000ms
     */
    mainThreadBlocked: createLogEvent('frontend.performance', 'main_thread_blocked')
  },

  // Player events - student-facing issues NOT logged by API
  player: {
    /**
     * Activity failed to load - student cannot start
     * When: Learning activity fails to load (before/after API call)
     */
    activityLoadError: createLogEvent('frontend.player', 'activity_load_error'),

    /**
     * Submission failed on client side - before reaching API
     * When: Client-side validation or error prevents submission
     */
    submissionFailed: createLogEvent('frontend.player', 'submission_failed'),

    /**
     * Autosave failed - risk of data loss
     * When: Autosave attempt fails (student keeps working but changes not saved)
     */
    autosaveFailed: createLogEvent('frontend.player', 'autosave_failed'),

    /**
     * Student stuck - 3+ consecutive errors in short time
     * When: Same student hits multiple errors, likely blocked from progressing
     */
    studentStuck: createLogEvent('frontend.player', 'student_stuck')
  }
} as const;
